import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import store from "store/store";
import { setAuth } from "store/auth/authSlice";
import { EXPIRED_SESSION_MESSAGE_KEY } from "constants/constants";
import { LOCAL_KEY } from "constants/key";
import { setLoadingPage } from "store/app/app.slice";
import { handleUnregisterServiceWorkers } from "utils/service-worker";
import { LANGUAGES } from "constants/constants";

const baseURL = process.env.REACT_APP_API_URL;

type AxiosData = AxiosRequestConfig["data"];
type AxiosParams = AxiosRequestConfig["params"];

const api = {
	isRefreshingToken: false,
	request: function <T>(
		config: {
			method: AxiosRequestConfig["method"];
			url: AxiosRequestConfig["url"];
			data?: AxiosData;
			params?: AxiosParams;
		},
		isFormData?: boolean,
	): Promise<AxiosResponse<T>> {
		const {
			auth: { auth },
			lang: { lang },
		} = store.getState() || {};

		const { jwt } = auth || {};
		return new Promise((resolve, reject) => {
			const headers = {
				mode: "no-cors",
				"Access-Control-Allow-Origin": "*",
				"ngrok-skip-browser-warning": true,
				Authorization: `Bearer ${jwt}`,
				"Content-Type": isFormData ? "multipart/form-data" : "application/json",
				"Accept-Language": lang || LANGUAGES.EN,
				"x-carz-device-id": localStorage.getItem(LOCAL_KEY.deviceId),
				// "ngrok-skip-browser-warning": "any",
				...(config.params &&
					config.params.version && {
						"x-carz-api-version": config.params.version,
					}),
			};
			axios({ baseURL, headers, withCredentials: true, ...config })
				.then(res => {
					resolve(res);
				})
				.catch(err => {
					if (!err.response) reject(err);
					const { status } = err.response;
					if (status === 401) {
						if (err?.config?.url?.includes("/logout")) return;
						if (auth) {
							localStorage.removeItem(LOCAL_KEY.deviceId);
							handleUnregisterServiceWorkers();
							store.dispatch(setAuth(null));
							store.dispatch(setLoadingPage(false));
							setTimeout(() => {
								const errorMessage = sessionStorage.getItem(
									EXPIRED_SESSION_MESSAGE_KEY,
								);
								if (!errorMessage) {
									sessionStorage.setItem(
										EXPIRED_SESSION_MESSAGE_KEY,
										"Your session has expired, please log in again",
									);
								}
							});
							return;
						}
					}
					reject(err.response.data);
				});
		});
	},
	refreshToken: async function () {
		const {
			auth: { auth },
		} = store.getState() || {};

		const { refreshToken = "", jwt = "" } = auth || {};
		try {
			this.isRefreshingToken = true;
			const res = await axios({
				baseURL,
				headers: {
					Authorization: `Bearer ${jwt}`,
					"Content-Type": "application/json",
				},
				withCredentials: true,
				url: "/auth/refreshtoken",
				method: "post",
				data: {
					refreshToken,
				},
			});
			store.dispatch(setAuth({ ...auth, ...res.data.data }));
			return true;
		} catch (error) {
			return false;
		} finally {
			this.isRefreshingToken = false;
		}
	},
	get: function <T>(url: string, params?: AxiosParams) {
		return this.request<T>({
			url,
			method: "get",
			params,
		});
	},
	post: function <T>(
		url: string,
		data: AxiosData,
		isFormData?: boolean,
		headerParams?: AxiosParams,
	) {
		return this.request<T>(
			{
				url,
				method: "post",
				data,
				...(headerParams && { params: headerParams }),
			},
			isFormData,
		);
	},
	put: function <T>(url: string, data: AxiosData, headerParams?: AxiosParams) {
		const config = {
			url,
			method: "put",
			data,
			...(headerParams && { params: headerParams }),
		};
		return this.request<T>(config);
	},
	patch: function <T>(
		url: string,
		data: AxiosData,
		headerParams?: AxiosParams,
	) {
		const config = {
			url,
			method: "patch",
			data,
			...(headerParams && { params: headerParams }),
		};
		return this.request<T>(config);
	},
	delete: function <T>(url: string) {
		return this.request<T>({
			url,
			method: "delete",
		});
	},
};

export default api;
